/**
 * A picture for a conversation, made small enough to live in a row.
 *
 * It is stored on the chat itself rather than as a member of the archive: a
 * member belongs to an import, and this belongs to the person's own choice.
 * That means the row carries the bytes, so the bytes have to be small — 256px
 * square, JPEG, which lands around 10 KB and well under the column's 64 KiB.
 *
 * Square-cropped from the centre rather than squashed, because every place it
 * appears is a circle and a squashed face in a circle looks broken.
 */
const SIDE = 256;

export async function squareJpeg(file: File): Promise<Uint8Array> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = SIDE; canvas.height = SIDE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot resize an image.');
  ctx.drawImage(bitmap,
    (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side,
    0, 0, SIDE, SIDE);
  bitmap.close();

  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.82));
  if (!blob) throw new Error('This browser could not encode the image.');
  return new Uint8Array(await blob.arrayBuffer());
}
